import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { 
  BarChart3, Globe, TrendingUp, Users, Calendar,
  RefreshCw, Filter, ChevronDown
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

// Country code to name mapping
const COUNTRY_NAMES = {
  'US': 'United States',
  'CA': 'Canada',
  'GB': 'United Kingdom',
  'AU': 'Australia',
  'DE': 'Germany',
  'FR': 'France',
  'IN': 'India',
  'BR': 'Brazil',
  'NL': 'Netherlands',
  'SE': 'Sweden',
  'IE': 'Ireland',
  'SG': 'Singapore',
  'NZ': 'New Zealand',
  'MX': 'Mexico',
  'ES': 'Spain',
  'IT': 'Italy',
  'JP': 'Japan',
  'KR': 'South Korea',
};

// Source icons/colors
const SOURCE_CONFIG = {
  'google': { color: 'bg-blue-500', label: 'Google' },
  'linkedin': { color: 'bg-sky-600', label: 'LinkedIn' },
  'facebook': { color: 'bg-indigo-600', label: 'Facebook' },
  'twitter': { color: 'bg-slate-700', label: 'Twitter/X' },
  'instagram': { color: 'bg-pink-500', label: 'Instagram' },
  'youtube': { color: 'bg-red-600', label: 'YouTube' },
  'reddit': { color: 'bg-orange-500', label: 'Reddit' },
  'bing': { color: 'bg-teal-500', label: 'Bing' },
  'direct': { color: 'bg-emerald-500', label: 'Direct' },
  'referral': { color: 'bg-purple-500', label: 'Referral' },
};

export default function AdminAnalyticsDashboard() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState('30'); // days
  const [eventType, setEventType] = useState('all');
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [sourceData, setSourceData] = useState([]);
  const [countryData, setCountryData] = useState([]);
  const [conversionData, setConversionData] = useState([]);

  const getDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - parseInt(dateRange));
    return {
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0]
    };
  };

  const fetchAnalytics = async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) return;

    const { start_date, end_date } = getDateRange();
    const eventParam = eventType !== 'all' ? `&event_type=${eventType}` : '';

    try {
      const [summaryRes, sourcesRes, countriesRes, conversionsRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/summary?start_date=${start_date}&end_date=${end_date}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/analytics/sources?start_date=${start_date}&end_date=${end_date}${eventParam}&limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/analytics/countries?start_date=${start_date}&end_date=${end_date}${eventParam}&limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/analytics/conversions-by-source-country?start_date=${start_date}&end_date=${end_date}&conversion_event=signup_complete&limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setSummary(summaryRes.data);
      setSourceData(sourcesRes.data.sources || []);
      setCountryData(countriesRes.data.countries || []);
      setConversionData(conversionsRes.data.conversions || []);
    } catch (error) {
      toast.error("Failed to load analytics data");
      console.error(error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange, eventType]);

  const handleRefresh = async () => {
    setRefreshing(true);
    
    // First trigger aggregation
    const token = localStorage.getItem("admin_token");
    try {
      const today = new Date().toISOString().split('T')[0];
      await axios.post(`${API}/admin/analytics/aggregate?target_date=${today}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Analytics refreshed");
    } catch (error) {
      console.error("Aggregation error:", error);
    }
    
    await fetchAnalytics();
  };

  const getSourceLabel = (source) => SOURCE_CONFIG[source]?.label || source;
  const getSourceColor = (source) => SOURCE_CONFIG[source]?.color || 'bg-gray-500';
  const getCountryName = (code) => COUNTRY_NAMES[code] || code;

  // Calculate totals for percentages
  const totalSourceEvents = sourceData.reduce((sum, s) => sum + s.count, 0);
  const totalCountryEvents = countryData.reduce((sum, c) => sum + c.count, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">Traffic Analytics</h1>
          <p className="text-muted-foreground mt-1">Privacy-preserving traffic insights</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[140px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Select value={eventType} onValueChange={setEventType}>
            <SelectTrigger className="w-[160px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              <SelectItem value="page_view">Page Views</SelectItem>
              <SelectItem value="signup_complete">Signups</SelectItem>
              <SelectItem value="login">Logins</SelectItem>
              <SelectItem value="question_asked">Questions</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <BarChart3 className="w-4 h-4" />
              <span className="text-sm">Total Events</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{summary.total_events?.toLocaleString() || 0}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">Traffic Sources</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{summary.unique_sources || 0}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Globe className="w-4 h-4" />
              <span className="text-sm">Countries</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{summary.unique_countries || 0}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Users className="w-4 h-4" />
              <span className="text-sm">Signups</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{summary.event_totals?.signup_complete?.toLocaleString() || 0}</p>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Traffic Sources */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Traffic Sources
          </h2>
          {sourceData.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No source data available</p>
          ) : (
            <div className="space-y-3">
              {sourceData.map((source, idx) => {
                const percentage = totalSourceEvents > 0 
                  ? ((source.count / totalSourceEvents) * 100).toFixed(1) 
                  : 0;
                return (
                  <div key={idx} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${getSourceColor(source.source)}`}></div>
                        <span className="text-foreground font-medium">{getSourceLabel(source.source)}</span>
                        <span className="text-muted-foreground text-xs">({source.event_type})</span>
                      </div>
                      <span className="text-foreground font-medium">{source.count.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${getSourceColor(source.source)}`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Traffic by Country */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" />
            Traffic by Country
          </h2>
          {countryData.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No country data available</p>
          ) : (
            <div className="space-y-3">
              {countryData.map((country, idx) => {
                const percentage = totalCountryEvents > 0 
                  ? ((country.count / totalCountryEvents) * 100).toFixed(1) 
                  : 0;
                return (
                  <div key={idx} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getFlagEmoji(country.country_code)}</span>
                        <span className="text-foreground font-medium">{getCountryName(country.country_code)}</span>
                        <span className="text-muted-foreground text-xs">({country.event_type})</span>
                      </div>
                      <div className="text-right">
                        <span className="text-foreground font-medium">{country.count.toLocaleString()}</span>
                        <span className="text-muted-foreground text-xs ml-2">({percentage}%)</span>
                      </div>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="h-2 rounded-full bg-primary"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Conversions by Source + Country */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          Signups by Source & Country
        </h2>
        {conversionData.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No conversion data available yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Source</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Country</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Signups</th>
                </tr>
              </thead>
              <tbody>
                {conversionData.map((row, idx) => (
                  <tr key={idx} className="border-b border-border/50 hover:bg-muted/50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${getSourceColor(row.source)}`}></div>
                        <span className="text-foreground">{getSourceLabel(row.source)}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span>{getFlagEmoji(row.country_code)}</span>
                        <span className="text-foreground">{getCountryName(row.country_code)}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-foreground font-medium">{row.conversions}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Privacy Note */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <p className="text-emerald-800 text-sm">
          <strong>Privacy Note:</strong> This dashboard uses first-party, privacy-preserving analytics. 
          No IP addresses are stored — country is derived ephemerally. No cookies, no user tracking, no third-party data sharing.
        </p>
      </div>
    </div>
  );
}

// Helper function to get flag emoji from country code
function getFlagEmoji(countryCode) {
  if (!countryCode || countryCode.length !== 2) return '🌍';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}
